<script setup>
import { ref, onMounted } from 'vue';
import { setMeta } from '@/utils/util';
import * as Diff from 'diff';

onMounted(() => {
  document.title = "Text Diff Tool - Compare Text Differences";
  setMeta("description", "Free online text comparison tool. Compare two texts and highlight the differences. Perfect for finding changes between text versions.")
  setMeta("keywords", "text diff,text compare,difference checker,text comparison,diff tool")
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Text Diff Tool",
    "description": "Online text comparison tool with real-time difference highlighting",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
});

const text1 = ref('');
const text2 = ref('');
const diffResult = ref([]);
const diffMode = ref('chars'); // 'chars', 'words', 'lines'

const computeDiff = () => {
  let diff;
  switch (diffMode.value) {
    case 'chars':
      diff = Diff.diffChars(text1.value, text2.value);
      break;
    case 'words':
      diff = Diff.diffWords(text1.value, text2.value);
      break;
    case 'lines':
      diff = Diff.diffLines(text1.value, text2.value);
      break;
  }
  diffResult.value = diff;
};

const clearTexts = () => {
  text1.value = '';
  text2.value = '';
  diffResult.value = [];
};

const getExampleTexts = () => {
  text1.value = 'Hello world!\nThis is a sample text.\nIt will be used for comparison.';
  text2.value = 'Hello World!\nThis is an example text.\nIt will be used to show differences.';
  computeDiff();
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">Text Diff Tool</h1>
    
    <div class="controls">
      <el-radio-group v-model="diffMode" @change="computeDiff">
        <el-radio-button label="chars">Character by Character</el-radio-button>
        <el-radio-button label="words">Word by Word</el-radio-button>
        <el-radio-button label="lines">Line by Line</el-radio-button>
      </el-radio-group>
      
      <div class="buttons">
        <el-button @click="computeDiff" type="primary">Compare</el-button>
        <el-button @click="clearTexts">Clear</el-button>
        <el-button @click="getExampleTexts">Load Example</el-button>
      </div>
    </div>

    <div class="input-area">
      <div class="text-input">
        <label>Original Text:</label>
        <el-input
          v-model="text1"
          type="textarea"
          :rows="10"
          placeholder="Enter the original text here..."
          @input="computeDiff"
        />
      </div>
      
      <div class="text-input">
        <label>Modified Text:</label>
        <el-input
          v-model="text2"
          type="textarea"
          :rows="10"
          placeholder="Enter the modified text here..."
          @input="computeDiff"
        />
      </div>
    </div>

    <div class="diff-result">
      <h3>Comparison Result:</h3>
      <div class="diff-content">
        <template v-for="(part, index) in diffResult" :key="index">
          <span
            :class="{
              'diff-added': part.added,
              'diff-removed': part.removed,
              'diff-normal': !part.added && !part.removed
            }"
          >{{ part.value }}</span>
        </template>
      </div>
    </div>

    <div class="introduce">
      <h2>What is Text Diff Tool?📝</h2>
      <p>
        Text Diff Tool is a utility that helps you compare two pieces of text and identify the differences between them.
        It's particularly useful when you need to:
      </p>
      
      <h3>Features</h3>
      <ul>
        <li>Compare texts character by character, word by word, or line by line</li>
        <li>Highlight additions and deletions</li>
        <li>Real-time comparison</li>
        <li>Clear visualization of differences</li>
      </ul>

      <h3>Use Cases</h3>
      <ul>
        <li>Compare code snippets</li>
        <li>Review text changes</li>
        <li>Check for plagiarism</li>
        <li>Track document revisions</li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.controls {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.buttons {
  display: flex;
  gap: 10px;
}

.input-area {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.text-input {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: bold;
    color: #606266;
  }
}

.diff-result {
  margin-top: 30px;
  
  h3 {
    margin-bottom: 15px;
    color: #303133;
  }
}

.diff-content {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  white-space: pre-wrap;
  font-family: monospace;
  line-height: 1.5;
}

.diff-added {
  background-color: #e6ffe6;
  color: #0a3d0a;
}

.diff-removed {
  background-color: #ffe6e6;
  color: #3d0a0a;
  text-decoration: line-through;
}

.diff-normal {
  color: #606266;
}

.introduce {
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #606266;
    }
  }
}

@media (max-width: 768px) {
  .input-area {
    grid-template-columns: 1fr;
  }
}
</style> 